/**
 * Calendario de mensualidades: vigencia, estados y orden obligatorio de pago.
 *
 * Vigencia de una mensualidad (contrato funcional §7):
 *   - Entra en vigencia el dia 1 del mes.
 *   - Vence el ULTIMO dia calendario de ese mismo mes.
 *   - Genera mora desde el dia 1 del mes SIGUIENTE.
 *   - Puede pagarse anticipadamente si el cargo ya fue generado.
 *
 * Las fechas se calculan con calendario real (febrero y bisiestos incluidos)
 * usando aritmetica UTC sobre fechas puras `YYYY-MM-DD`.
 */

import type { OperationalDate } from './clock.js';
import { assertDomain } from './errors.js';
import type { Ulid } from './ids.js';
import { type Money, zero } from './money.js';
import {
  type MonthlyChargeState,
  outstandingCapital,
  outstandingLateFee,
  totalOutstanding,
} from './lateFees.js';

export const MONTH_NAMES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
] as const;

export function monthName(month: number): string {
  assertDomain(
    Number.isInteger(month) && month >= 1 && month <= 12,
    'NUMERACION_DIA_INVALIDO',
    'Mes inválido',
    { month },
  );
  return MONTH_NAMES[month - 1] as string;
}

/** Etiqueta que SIEMPRE incluye el año, para no confundir meses homónimos entre ciclos. */
export function monthLabel(year: number, month: number): string {
  return `${monthName(month)} ${year}`;
}

export interface MonthPeriod {
  readonly year: number;
  readonly month: number;
  /** Dia 1 del mes. */
  readonly startDate: OperationalDate;
  /** Ultimo dia calendario del mes. */
  readonly dueDate: OperationalDate;
  /** Dia 1 del mes siguiente: primer dia con mora. */
  readonly lateFeeStartDate: OperationalDate;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Ultimo dia del mes segun calendario real.
 * `Date.UTC(year, month, 0)` devuelve el ultimo dia del mes anterior a `month`
 * (los meses de `Date` son base 0), asi que pasar el mes 1-12 da justo lo buscado.
 * Febrero de un año bisiesto devuelve 29 sin tablas propias.
 */
export function lastDayOfMonth(year: number, month: number): number {
  assertDomain(
    Number.isInteger(year) && year >= 1900 && year <= 2999,
    'NUMERACION_DIA_INVALIDO',
    'Año inválido',
    { year },
  );
  assertDomain(
    Number.isInteger(month) && month >= 1 && month <= 12,
    'NUMERACION_DIA_INVALIDO',
    'Mes inválido',
    { month },
  );
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function isLeapYear(year: number): boolean {
  return lastDayOfMonth(year, 2) === 29;
}

/** Calcula inicio, vencimiento y primer dia de mora de una mensualidad. */
export function monthPeriod(year: number, month: number): MonthPeriod {
  const last = lastDayOfMonth(year, month);
  const nextYear = month === 12 ? year + 1 : year;
  const nextMonth = month === 12 ? 1 : month + 1;
  return Object.freeze({
    year,
    month,
    startDate: `${year}-${pad(month)}-01`,
    dueDate: `${year}-${pad(month)}-${pad(last)}`,
    lateFeeStartDate: `${nextYear}-${pad(nextMonth)}-01`,
  });
}

/** Los doce periodos de un ciclo, en orden. */
export function cycleMonths(year: number): MonthPeriod[] {
  return Array.from({ length: 12 }, (_, index) => monthPeriod(year, index + 1));
}

// ---------------------------------------------------------------------------
// Estados
// ---------------------------------------------------------------------------

export const MONTH_STATUSES = [
  'SIN_CARGO', // el cargo aun no ha sido generado por Administracion
  'FUTURO', // el cargo existe pero el mes todavia no inicia
  'PENDIENTE', // vigente, sin abonos
  'PARCIAL', // vigente o vencido, con abonos y saldo
  'PAGADO',
  'VENCIDO', // paso su ultimo dia y conserva saldo
  'CANCELADO',
] as const;
export type MonthStatus = (typeof MONTH_STATUSES)[number];

export const MONTH_STATUS_LABELS: Readonly<Record<MonthStatus, string>> = Object.freeze({
  SIN_CARGO: 'Sin cargo',
  FUTURO: 'Futuro',
  PENDIENTE: 'Pendiente',
  PARCIAL: 'Parcial',
  PAGADO: 'Pagado',
  VENCIDO: 'Vencido',
  CANCELADO: 'Cancelado',
});

/**
 * Simbolo textual que acompaña al estado. La interfaz NUNCA depende solo del color:
 * cada estado se distingue por texto y por simbolo.
 */
export const MONTH_STATUS_SYMBOLS: Readonly<Record<MonthStatus, string>> = Object.freeze({
  SIN_CARGO: '·',
  FUTURO: '◷',
  PENDIENTE: '○',
  PARCIAL: '◐',
  PAGADO: '✓',
  VENCIDO: '!',
  CANCELADO: '✕',
});

/** Una celda del calendario: el mes, su cargo (si existe) y sus cifras ya calculadas. */
export interface CalendarMonth {
  readonly cycleId: Ulid;
  readonly cycleYear: number;
  readonly month: number;
  readonly period: MonthPeriod;
  /** `null` si Administracion todavia no generó el cargo de ese mes. */
  readonly chargeId: Ulid | null;
  readonly status: MonthStatus;
  readonly originalAmount: Money;
  readonly capitalOutstanding: Money;
  readonly lateFee: Money;
  readonly paid: Money;
  readonly totalOutstanding: Money;
  readonly lateFeeWaived: boolean;
  /** `true` si puede incluirse en un pago. */
  readonly selectableForPayment: boolean;
  /** `true` si puede incluirse en una exoneracion de mora. */
  readonly selectableForWaiver: boolean;
}

export function computeMonthStatus(
  state: MonthlyChargeState | null,
  period: MonthPeriod,
  asOf: OperationalDate,
): MonthStatus {
  if (state === null) return 'SIN_CARGO';
  if (state.cancelled) return 'CANCELADO';

  const capital = outstandingCapital(state);
  const lateFee = outstandingLateFee(state, asOf);
  if (capital.minor === 0 && lateFee.minor === 0) return 'PAGADO';

  if (asOf < period.startDate) return 'FUTURO';
  // VENCIDO tiene prioridad sobre PARCIAL a proposito: un mes vencido con abono
  // sigue vencido y sigue generando mora, y eso es lo que el cajero debe ver
  // primero. El abono no se pierde de vista: viaja aparte en `paidMinor`.
  if (asOf > period.dueDate) return 'VENCIDO';
  return state.capitalPaid.minor > 0 || state.lateFeePaid.minor > 0 ? 'PARCIAL' : 'PENDIENTE';
}

/** `true` si el mes puede incluirse en un pago (§14: SIN_CARGO, PAGADO y CANCELADO no). */
export function isSelectableForPayment(status: MonthStatus): boolean {
  return status === 'PENDIENTE' || status === 'PARCIAL' || status === 'VENCIDO' || status === 'FUTURO';
}

/** Ensambla la celda del calendario a partir del estado del cargo. */
export function buildCalendarMonth(params: {
  cycleId: Ulid;
  cycleYear: number;
  month: number;
  chargeId: Ulid | null;
  state: MonthlyChargeState | null;
  asOf: OperationalDate;
  currency: string;
}): CalendarMonth {
  const period = monthPeriod(params.cycleYear, params.month);
  const status = computeMonthStatus(params.state, period, params.asOf);
  const state = params.state;

  const originalAmount = state?.originalAmount ?? zero(params.currency);
  const capital = state === null ? zero(params.currency) : outstandingCapital(state);
  const lateFee = state === null ? zero(params.currency) : outstandingLateFee(state, params.asOf);
  const total = state === null ? zero(params.currency) : totalOutstanding(state, params.asOf);
  const paid =
    state === null
      ? zero(params.currency)
      : { minor: state.capitalPaid.minor + state.lateFeePaid.minor, currency: params.currency };

  return Object.freeze({
    cycleId: params.cycleId,
    cycleYear: params.cycleYear,
    month: params.month,
    period,
    chargeId: params.chargeId,
    status,
    originalAmount,
    capitalOutstanding: capital,
    lateFee,
    paid,
    totalOutstanding: total,
    lateFeeWaived: state?.lateFeeWaived ?? false,
    selectableForPayment: params.chargeId !== null && isSelectableForPayment(status),
    // Solo meses con mora acumulada mayor que cero (§8 regla 13).
    selectableForWaiver: params.chargeId !== null && lateFee.minor > 0 && !(state?.lateFeeWaived ?? false),
  });
}

// ---------------------------------------------------------------------------
// Orden obligatorio de mensualidades (§15)
// ---------------------------------------------------------------------------

export interface SelectionCheck {
  readonly valid: boolean;
  /**
   * Mensualidades pendientes ANTERIORES a la ultima seleccionada que faltan.
   * La interfaz puede autoseleccionarlas (regla 6) o explicar y exigir corregir.
   */
  readonly missing: readonly CalendarMonth[];
  readonly message: string | null;
}

function chronologicalKey(month: CalendarMonth): number {
  return month.cycleYear * 100 + month.month;
}

/**
 * Verifica el orden obligatorio de mensualidades.
 *
 * Las siete reglas del contrato se reducen a UNA invariante comprobable:
 *
 *   toda mensualidad con saldo ANTERIOR a la ultima seleccionada
 *   debe estar tambien seleccionada.
 *
 * De ahi se derivan: no se paga un mes futuro dejando uno vencido (reglas 1-3),
 * la seleccion es consecutiva (regla 4) y no se puede saltar un mes intermedio
 * (regla 5). Los meses de distinto ciclo se ordenan por año y mes, de modo que la
 * regla cruza ciclos igual que dentro de uno.
 *
 * Los conceptos que no son mensualidades no entran aqui: no alteran el orden (regla 7).
 */
export function checkMonthlySelectionOrder(
  months: readonly CalendarMonth[],
  selectedChargeIds: readonly Ulid[],
): SelectionCheck {
  const selected = new Set(selectedChargeIds);
  const withCharge = months.filter((month) => month.chargeId !== null);
  const chosen = withCharge.filter((month) => selected.has(month.chargeId as Ulid));

  if (chosen.length === 0) {
    return { valid: true, missing: [], message: null };
  }

  const latestKey = Math.max(...chosen.map(chronologicalKey));

  const missing = withCharge
    .filter((month) => chronologicalKey(month) < latestKey)
    .filter((month) => month.totalOutstanding.minor > 0)
    .filter((month) => month.status !== 'CANCELADO')
    .filter((month) => !selected.has(month.chargeId as Ulid))
    .sort((a, b) => chronologicalKey(a) - chronologicalKey(b));

  if (missing.length === 0) {
    return { valid: true, missing: [], message: null };
  }

  const listado = missing.map((month) => monthLabel(month.cycleYear, month.month)).join(', ');
  return {
    valid: false,
    missing,
    message:
      missing.length === 1
        ? `Antes de pagar meses posteriores debe cubrir ${listado}.`
        : `Antes de pagar meses posteriores debe cubrir estos meses pendientes: ${listado}.`,
  };
}

/** Version estricta para el servidor: la misma regla, pero lanza. */
export function assertMonthlySelectionOrder(
  months: readonly CalendarMonth[],
  selectedChargeIds: readonly Ulid[],
): void {
  const check = checkMonthlySelectionOrder(months, selectedChargeIds);
  assertDomain(check.valid, 'DISTRIBUCION_NO_CUADRA', check.message ?? 'Selección inválida', {
    faltantes: check.missing.map((month) => monthLabel(month.cycleYear, month.month)),
  });
}

/**
 * Completa una seleccion agregando las mensualidades pendientes intermedias
 * (regla 6, variante "autoseleccionar"). Devuelve la seleccion ya ordenada.
 */
export function completeMonthlySelection(
  months: readonly CalendarMonth[],
  selectedChargeIds: readonly Ulid[],
): Ulid[] {
  const check = checkMonthlySelectionOrder(months, selectedChargeIds);
  const completed = new Set(selectedChargeIds);
  for (const month of check.missing) {
    if (month.chargeId !== null) completed.add(month.chargeId);
  }
  const order = new Map(
    months
      .filter((month) => month.chargeId !== null)
      .map((month) => [month.chargeId as Ulid, chronologicalKey(month)]),
  );
  return [...completed].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
}
